/**
 * Telegram initData-exchange auth provider (ADR-082, developer's unit 3).
 *
 * Implements the app-core `Auth` seam for the mini-app. The flow (all opaque to
 * app-core — it only ever calls `getAccessToken`):
 *
 *   1. read the raw `Telegram.WebApp.initData` blob (never parsed/validated here),
 *   2. POST it to the gateway's PUBLIC initData→session endpoint,
 *   3. hold the returned session (short-TTL bearer + rotating refresh credential),
 *   4. `getAccessToken` returns a live bearer, refreshing transparently.
 *
 * Contract PINNED at proto v0.26.0 (agnivo/tenant/v1/session.proto,
 * MintMiniAppSession; supersedes the 07-09 "path TBD" working shapes this hook
 * was first written against — the drift was caught live, dev triage 2026-07-13):
 *   - Request:  POST { rawInitData } (proto raw_init_data, REQUIRED) to the
 *               UNAUTHENTICATED mint route (config.tgSessionPath).
 *   - Success:  { accessToken, accessExpiresAt, refreshToken, refreshExpiresAt }
 *               (Timestamps as RFC3339 strings). accessToken is SHORT-TTL
 *               (5–15 min); you CANNOT re-post initData to refresh
 *               (frozen-at-launch, single-use replay ledger). refresh_token MAY
 *               be EMPTY in the first cut (RefreshMiniAppSession is a
 *               fast-follow, NOT in v0.26.0) — an empty string means
 *               "re-bootstrap on expiry", never call the refresh route.
 *   - Failure:  first-touch is AUTO-PROVISIONED (no "not linked" UX). Distinct
 *               signals: forged initData → hard fail; EXPIRED initData → re-read
 *               a fresh blob and retry ("reconnecting", not an error); rate-limit
 *               and transient-unavailable → retryable. A REJECTED fetch (CORS /
 *               network down — the browser gives no status) is 'unavailable':
 *               it must land on the LaunchGate recovery screen, NEVER hang
 *               'loading' (the 2026-07-13 first-tap defect).
 */
import { useEffect, useMemo, useState } from 'react';
import { isApiError, ApiError, type Auth, type AuthStatus, type AuthUser } from '@agnivo/app-core';
import {
  createFetchClient,
  type AgnivoClient,
  type MintMiniAppSessionResponse,
} from '@agnivo/api-client';
import {
  getTelegramWebApp,
  readInitDataFromHash,
  resolveInitDataSource,
  setInitDataSource,
  waitForTelegramWebApp,
} from '../telegram';
import { reportTguidMissing } from '../telemetry/miniappTguidMissingSender';
import { setLiveAccessTokenAccessor } from './liveAccessToken';

/**
 * The tg_uid-missing recovery signal (architect ruling 2026-07-15, ADR-082).
 * A mini-app session minted before the tg_uid rollout can't gain tg_uid in-session
 * (frozen initData → replay-consumed + EXPIRED; refresh carries the NULL row) — only
 * a relaunch fixes it. A tg_uid-gated call therefore fails with this ErrorInfo, and
 * the client routes to the reopen surface. Match the (domain, reason) TUPLE, NOT the
 * gRPC code (distinct recovery signals share codes deliberately). CLASS scope: ANY
 * call carrying this tuple triggers it, not just resolveConversation.
 */
export const TGUID_MISSING_DOMAIN = 'gateway.agnivo.org';
export const TGUID_MISSING_REASON = 'MINIAPP_TGUID_MISSING';

/** True iff the error carries the tg_uid-missing (domain, reason) tuple. */
export function isTgUidMissing(err: unknown): boolean {
  if (!isApiError(err)) return false;
  const info = err.errorInfo;
  return info?.domain === TGUID_MISSING_DOMAIN && info?.reason === TGUID_MISSING_REASON;
}

/** Renew a few seconds before real expiry so an in-flight request never races it. */
const EXPIRY_SKEW_MS = 15_000;

interface SessionPair {
  accessToken: string;
  expiresAtMs: number;
  refreshToken: string | null;
  refreshExpiresAtMs: number | null;
}

/**
 * How an exchange failed. grpc-gateway maps google.rpc.Status → HTTP; developer
 * surfaces EXPIRED distinctly at the edge (MC separates INVALID_HASH / EXPIRED /
 * MALFORMED, proto!109). EXPIRED discriminator pins via the ErrorInfo
 * `(domain, reason)` tuple returned in the Status details — Mirrors what
 * `api-client`'s ApiError.errorInfo exposes (`gateway.agnivo.org`,
 * `TELEGRAM_INIT_DATA_EXPIRED`).
 */
type ExchangeError =
  | 'expired'
  | 'forged'
  | 'rate_limited'
  | 'unavailable'
  | 'unknown'
  | 'tg_uid_missing'
  /** No launch payload at all: Telegram.WebApp.initData empty (a Telegram-client
   *  gap — observed live on desktop Telegram 2026-07-18, Dmitry's laptop — or
   *  the page opened outside Telegram). TERMINAL for this launch: there is
   *  nothing to exchange and no re-read can produce it — a retry would be an
   *  instant silent no-op (the exact dead-«Повторить» defect this kind fixes). */
  | 'no_launch_payload';

/**
 * Map a typed api-client ApiError → the discriminated ExchangeError. Reads the
 * (httpStatus, errorInfo.reason) TUPLE — the right place the wire carries it
 * after the move from raw-body probes (gateway returns ErrorInfo in the
 * `details` of `google.rpc.Status`, which is what api-client's ApiError
 * surfaces via `.errorInfo`).
 *
 * Status codes preserved verbatim from the prior `classifyError(httpStatus,
 * body)` so no signal changes value:
 *  - 0   → unavailable (rejected fetch: CORS / network — no status exists)
 *  - 401 + reason matches /EXPIR/i → expired (re-readable blob → relaunch)
 *  - 401 + otherwise → forged
 *  - 429 → rate_limited
 *  - 503 → unavailable
 *  - any other → unknown
 */
function classifyApiError(err: ApiError): ExchangeError {
  const reason = err.errorInfo?.reason ?? '';
  if (err.httpStatus === 0) return 'unavailable';
  if (err.httpStatus === 401) {
    return /EXPIR/i.test(reason) ? 'expired' : 'forged';
  }
  if (err.httpStatus === 429) return 'rate_limited';
  if (err.httpStatus === 503) return 'unavailable';
  return 'unknown';
}

/**
 * Whether a retry (`login()` → re-POST the SAME frozen initData) can plausibly
 * recover — drives the honest recovery UX (Auth.retryable). A SPENT/tampered blob
 * ('expired'/'forged') cannot succeed in this launch — only a genuine relaunch
 * delivers fresh initData — so we must NOT offer a dead retry there. Transient
 * ('unavailable'/'rate_limited') or 'unknown' → a re-POST may work, keep retry.
 * null (authed / no error) → undefined (no classification).
 *
 * COUPLED to `classifyApiError`: 'unknown' defaults toward retryable (safe UX —
 * offer the retry when we can't prove it's terminal). If a future TERMINAL error
 * kind is added, add it BOTH to classifyApiError (to produce it) AND to the
 * not-retryable set here (else it'd wrongly keep a dead retry).
 *
 * 'tg_uid_missing' is terminal but NOT produced by classifyApiError — it's set
 * out-of-band by `markSessionFatal` when a live API call returns the
 * (gateway.agnivo.org, MINIAPP_TGUID_MISSING) signal (a pre-tg_uid session that
 * can't recover in-session; ADR-082, architect ruling 2026-07-15). Same reopen
 * remedy as 'expired'/'forged' → not retryable.
 */
export function isRetryable(e: ExchangeError | null): boolean | undefined {
  if (e === null) return undefined;
  return e !== 'expired' && e !== 'forged' && e !== 'tg_uid_missing' && e !== 'no_launch_payload';
}

function toPair(r: MintMiniAppSessionResponse): SessionPair {
  return {
    accessToken: r.accessToken,
    expiresAtMs: Date.parse(r.accessExpiresAt ?? ''),
    // `|| null` (not ??): an EMPTY refresh_token means "no refresh credential
    // yet, re-bootstrap on expiry" (proto v0.26.0 doc) — it must never arm the
    // refresh path against the not-yet-existing refresh RPC.
    refreshToken: r.refreshToken || null,
    refreshExpiresAtMs: r.refreshExpiresAt ? Date.parse(r.refreshExpiresAt) : null,
  };
}

/**
 * Session holder. One instance per shell (held in a ref). Serialises
 * bootstrap/refresh through a single in-flight promise so concurrent
 * `getAccessToken` callers (many requests firing at once) share one exchange.
 */
export class TelegramInitDataSession {
  status: AuthStatus = 'loading';
  user: AuthUser | null = null;
  /** Why the last exchange failed — kept for tailored recovery copy at go-live
   * (dev surfaces EXPIRED distinctly from a forged blob). null when authed. */
  lastError: ExchangeError | null = null;

  private pair: SessionPair | null = null;
  private inflight: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();
  /**
   * A typed client dedicated to the unauthenticated mint/refresh routes. The
   * chat api-client (the one wired into <ApiProvider>) cannot serve this
   * surface — these routes REQUIRE no bearer, and the chat client constructs
   * `getAccessToken` from THIS session's pair, which is the chicken-and-egg.
   *
   * Lifted into api-client in this MR (was previously hand-rolled `fetch`
   * in `post()`) so future test/mock consumers get the typed
   * `auth.telegramSession({ rawInitData })` / `auth.telegramRefresh({
   * refreshToken })` shape without reinventing it. See
   * `backlog/done/2026-08-05-frontend-v1-inventory-client-auth-telegram-and-agents-clone-mock.md`.
   */
  private readonly authClient: AgnivoClient;

  constructor(baseUrl: string) {
    const base = baseUrl.replace(/\/$/, '');
    this.authClient = createFetchClient({
      baseUrl: base,
      // UNAUTHENTICATED bootstrap: api-client's transport stamps Authorization
      // only when getAccessToken returns truthy, so a `null` here is the
      // intentional shape — the gateway validates initData on its own.
      getAccessToken: async () => null,
    });
    // bound once → stable identity for app-core's ApiProvider useMemo dep
    this.getAccessToken = this.getAccessToken.bind(this);
    this.bootstrap = this.bootstrap.bind(this);
    this.logout = this.logout.bind(this);
    this.handleApiError = this.handleApiError.bind(this);
    const webApp = getTelegramWebApp();
    // initDataUnsafe guarded too: a bridge that ships no launch payload (the
    // desktop-Telegram gap) may omit it entirely — an unguarded read would
    // crash at construction, before any recovery UI can render.
    const u = webApp?.initDataUnsafe?.user;
    if (u) this.user = { name: [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || 'Пользователь' };
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    for (const fn of this.listeners) fn();
  }

  private setStatus(status: AuthStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.emit();
  }

  private hasValidAccess(): boolean {
    return this.pair !== null && Number.isFinite(this.pair.expiresAtMs) && Date.now() < this.pair.expiresAtMs - EXPIRY_SKEW_MS;
  }

  private canRefresh(): boolean {
    return (
      this.pair?.refreshToken != null &&
      (this.pair.refreshExpiresAtMs === null || Date.now() < this.pair.refreshExpiresAtMs - EXPIRY_SKEW_MS)
    );
  }

  /**
   * Acquire a fresh session from initData. Any failure lands on unauthenticated
   * (retryable via login); LaunchGate renders the reopen-from-Telegram recovery.
   */
  async bootstrap(): Promise<void> {
    if (this.inflight) return this.inflight;
    this.inflight = this.doBootstrap();
    try {
      await this.inflight;
    } finally {
      this.inflight = null;
    }
  }

  private async doBootstrap(): Promise<void> {
    // Race the bridge: HTML's <script src="telegram.org/js/telegram-web-app.js">
    // is async, and the SPA's useEffect can fire BEFORE the SDK finishes loading
    // on a cold cache (browser-smoke test 3 of quality-tests #2757985481 lost
    // this race; network trace showed the SDK still pending at the 25s budget).
    // 200ms covers the typical <100ms SDK load on Telegram's CDN with 2×
    // headroom for transient slow loads; on `false` the URL-fragment fallback
    // below is the long-tail path. See `waitForTelegramWebApp` in telegram.ts.
    const webApp = (await waitForTelegramWebApp(200)) ? getTelegramWebApp() : null;
    // Bridge first; URL-fragment fallback second (BotFather-works-here probe,
    // Dmitry 2026-07-18: the desktop client CAN run mini-apps, so when the
    // bridge yields nothing but Telegram put the signed payload in the hash,
    // use it — the gateway validates the signature regardless of transport).
    const hashInitData = readInitDataFromHash();
    const initData = webApp?.initData || hashInitData;
    // WHICH transport supplied the payload, published for the browser smoke.
    //
    // Asked for by qe (2026-08-17): `miniapp-launch.spec.ts` can currently only
    // observe that auth SUCCEEDED, and the two transports above are a fallback
    // pair — so a sustained-green run is equally consistent with "the SDK path
    // works" and "telegram.org has been down for days and the hash fallback has
    // been carrying every launch." Those want opposite responses, and nothing
    // distinguished them. Same green-ambiguity shape as web!327's
    // `data-auth-state`: the marker is the frontend half, the assertion is qe's.
    //
    // On `documentElement`, not on a LaunchGate element, because the interesting
    // case is the SUCCESSFUL launch — and on success LaunchGate renders bare
    // `children` with no wrapper of its own. Adding one to host an attribute
    // would put a layout box in the tree for a test's benefit. The root element
    // is also the established site for cross-cutting state here (app-core's
    // theme system sets `data-theme` on it).
    //
    // Set on every bootstrap including the empty case, and NOT cleared on
    // failure: `'none'` is a real, distinct answer to "which transport
    // supplied it" and is exactly the state `no_launch_payload` describes.
    // Leaving the attribute absent instead would make "no payload" and "this
    // build predates the attribute" the same observation — the vacuity that
    // web!327 was written to remove, reintroduced one level down.
    setInitDataSource(resolveInitDataSource(webApp?.initData, hashInitData));
    if (!initData) {
      // No launch payload ANYWHERE — nothing to exchange, and nothing a
      // same-launch retry could change. Land TERMINAL (not the generic
      // transient screen): before this classification the gate showed
      // «проверьте соединение» + a retry that no-opped without a network call
      // (live repro: desktop Telegram, 2026-07-18 — zero mint RPCs at the
      // gateway for the window). LaunchGate renders a render-time diagnostic
      // code (bridge? hash?) beside the copy so a user report pins the
      // launch path without any state threading.
      this.lastError = 'no_launch_payload';
      this.setStatus('unauthenticated');
      return;
    }
    if (this.status !== 'authenticated') this.setStatus('loading');
    try {
      // wire pin: proto raw_init_data → JSON rawInitData (v0.26.0)
      const response = await this.authClient.auth.telegramSession({ rawInitData: initData });
      const pair = toPair(response);
      // a session whose expiry doesn't parse would silently re-bootstrap on
      // EVERY getAccessToken and burn the single-use ledger — refuse it instead
      if (!Number.isFinite(pair.expiresAtMs)) {
        this.lastError = 'unknown';
        this.pair = null;
        this.setStatus('unauthenticated');
        return;
      }
      this.pair = pair;
      this.lastError = null;
      this.setStatus('authenticated');
      return;
    } catch (err) {
      // Network-level failure (rejected fetch / CORS / DNS): api-client's transport
      // surfaces these as ApiError with httpStatus=0 (see transport.ts) — same
      // shape as the prior `post({ ok: false, status: 0, body: null })` path. So
      // the classifier's httpStatus key is preserved, only the source of `reason`
      // moved from `body.reason` to `err.errorInfo.reason` (which is where the
      // wire actually carries it, per google.rpc.Status).
      // initData is FROZEN for the lifetime of a launch (ADR-082 #2/#3), so there
      // is no useful in-session retry — even an EXPIRED blob only refreshes on a
      // genuine relaunch (the user reopening the mini-app), which remounts this
      // hook with a fresh payload. So every failure lands on unauthenticated and
      // LaunchGate shows the reopen recovery. `lastError` preserves the
      // forged-vs-expired distinction (dev surfaces EXPIRED distinctly) for
      // tailored copy once the endpoint is live.
      this.lastError = err instanceof ApiError ? classifyApiError(err) : 'unknown';
      this.pair = null;
      // doBootstrap always transitions through 'loading' first (above), so this
      // setStatus always emits (…→'unauthenticated') even on a repeat failure —
      // and the hook tracks lastError, so the recovery UI re-reads `retryable` when
      // the failure KIND changes (e.g. transient → now-expired), never stranding a
      // dead retry.
      this.setStatus('unauthenticated');
    }
  }

  private async doRefresh(): Promise<void> {
    // Wire shape VERIFIED against merged proto!126 (RefreshTelegramSession):
    //   - request  { refreshToken } → gateway RefreshTelegramSessionRequest.refresh_token
    //     (grpc-gateway camelCase; field REQUIRED);
    //   - response = the mint authority's type (MintMiniAppSessionResponse, house
    //     style) → toPair handles it unchanged;
    //   - the refresh token ROTATES (consumed → a NEW one returned in the chain),
    //     and `this.pair = toPair(body)` REPLACES the whole pair, so the new
    //     refresh_token is adopted. Arms once the Go side populates a non-empty
    //     refresh_token (canRefresh() gate); dormant until then (empty → null).
    if (!this.pair?.refreshToken) return this.doBootstrap();
    try {
      const response = await this.authClient.auth.telegramRefresh({
        refreshToken: this.pair.refreshToken,
      });
      this.pair = toPair(response);
      this.setStatus('authenticated');
      return;
    } catch {
      // refresh lapsed/revoked → re-bootstrap from initData (re-open still works
      // inside a live WebView; standalone it lands on unauthenticated)
      return this.doBootstrap();
    }
  }

  /** Live bearer for the api-client, refreshing transparently. null = no session. */
  async getAccessToken(): Promise<string | null> {
    if (this.inflight) await this.inflight;
    if (this.hasValidAccess()) return this.pair!.accessToken;
    this.inflight = this.canRefresh() ? this.doRefresh() : this.doBootstrap();
    try {
      await this.inflight;
    } finally {
      this.inflight = null;
    }
    return this.hasValidAccess() ? this.pair!.accessToken : null;
  }

  login(): void {
    void this.bootstrap();
  }

  logout(): void {
    this.pair = null;
    this.setStatus('unauthenticated');
    // no server-side session-kill call yet (lands with the refresh store, ADR-082)
  }

  /**
   * Mark the session UNRECOVERABLE with a terminal reason (drives Auth.retryable
   * false + Auth.terminalReason). Used for a failure a re-POST can't fix — today
   * the tg_uid-missing signal, whose only remedy is a relaunch (fresh initData).
   */
  markSessionFatal(kind: ExchangeError): void {
    this.lastError = kind;
    this.pair = null;
    if (this.status === 'unauthenticated') this.emit(); // status same → force a re-render for the new reason
    else this.setStatus('unauthenticated');
  }

  /**
   * Observe a live api-client error (wired to ApiProvider.onApiError). CLASS scope:
   * any tg_uid-gated call carrying the (domain, reason) tuple terminates the session
   * → reopen surface. Side-effect-only; ignores everything else.
   *
   * `reportTguidMissing` fires HERE, at the actual classification event, not from a
   * React effect watching `lastError` — this runs exactly once per real API error
   * (`onApiError` is called from a genuine network response), so there is no
   * StrictMode double-mount/double-fire risk to guard against, unlike
   * `RouteViewReporter`'s ref-vs-useMemo concern for its own sender.
   */
  handleApiError(err: ApiError): void {
    if (isTgUidMissing(err)) {
      this.markSessionFatal('tg_uid_missing');
      reportTguidMissing({
        step: 'error_received',
        errorInfoReason: TGUID_MISSING_REASON,
      });
    }
  }
}

/**
 * Hook the shell uses to build the mini-app's `Auth`. Kicks off the initData
 * exchange on mount and re-renders on status changes (for the launch gate).
 * The returned `getAccessToken`/`login`/`logout` are identity-stable (bound on
 * the session instance) so app-core's ApiProvider doesn't rebuild its client.
 *
 * Returns `onApiError` alongside `auth`: the shell wires it into
 * `<ApiProvider onApiError={...}>` so a tg_uid-missing signal on ANY live call
 * terminates the session → reopen surface (identity-stable, bound on the session).
 */
export function useTelegramInitDataAuth(apiBaseUrl: string): {
  auth: Auth;
  onApiError: (err: ApiError) => void;
} {
  // Lazy state (not a ref) so app-core's hook lint (react-hooks/refs) allows
  // reading the session's fields during render. The instance is created once and
  // never replaced — state is used purely as a stable holder.
  const [session] = useState(() => new TelegramInitDataSession(apiBaseUrl));

  const [status, setStatus] = useState<AuthStatus>(session.status);
  const [user, setUser] = useState<AuthUser | null>(session.user);
  const [lastError, setLastError] = useState<ExchangeError | null>(session.lastError);

  useEffect(() => {
    // Registers the bound, identity-stable `getAccessToken` for the module-scope
    // telemetry senders that cannot call a hook to reach it (`liveAccessToken.ts`).
    // In an effect, not the constructor — the same StrictMode-double-mount reason
    // `RouteViewReporter` documents for its own ref: this cleans up and re-registers
    // on the real committed session, never a discarded one.
    setLiveAccessTokenAccessor(session.getAccessToken);
    const unsub = session.subscribe(() => {
      setStatus(session.status);
      setUser(session.user);
      setLastError(session.lastError); // re-render when the failure kind changes
    });
    void session.bootstrap();
    return () => {
      unsub();
      setLiveAccessTokenAccessor(null);
    };
  }, [session]);

  const auth = useMemo<Auth>(
    () => ({
      status,
      user,
      getAccessToken: session.getAccessToken,
      login: session.login.bind(session),
      logout: session.logout,
      retryable: isRetryable(lastError),
      // surfaces the terminal kind (e.g. 'tg_uid_missing') so LaunchGate can pick
      // tailored reopen copy; undefined when authed/loading (lastError null)
      terminalReason: lastError ?? undefined,
    }),
    [status, user, lastError, session],
  );
  return { auth, onApiError: session.handleApiError };
}
